import { memo, useCallback } from 'react';

import { CheckIcon, CopyIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { useCopyLinkToClipboard } from '~/hooks/useCopyLinkToClipboard';

type CopyLinkButtonProps = {
  onCopied: (url: string) => void;
  shareUrl: string;
  className?: string;
};

export const CopyLinkButton = memo(({ onCopied, shareUrl, className }: CopyLinkButtonProps) => {
  const { copyLinkToClipboard, copied } = useCopyLinkToClipboard();

  const onClick = useCallback(() => {
    // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the icon migration
    copyLinkToClipboard(shareUrl, onCopied);
  }, [copyLinkToClipboard, shareUrl, onCopied]);

  return (
    <Button
      data-testid="COPY_URL_BUTTON"
      appearance="filled"
      color="blue"
      onClick={onClick}
      size="medium"
      className={cn('gap-1', className)}
      prefix={copied ? <CheckIcon size="sm" /> : <CopyIcon size="sm" />}
    >
      {copied ? 'Link copied' : 'Copy link'}
    </Button>
  );
});

CopyLinkButton.displayName = 'CopyLinkButton';
